type Pending<TReq, TRes> = {
  req: TReq;
  resolve: (res: TRes) => void;
  reject: (error: unknown) => void;
};

const chunk = <T>(items: T[], size: number): T[][] =>
  Array.from({ length: Math.ceil(items.length / size) }, (_, i) =>
    items.slice(i * size, (i + 1) * size)
  );

/**
 * Collects requests made within `windowMs` and loads them together: one `loadBatch` call per batch
 * key and per `maxBatchSize` requests. `loadBatch` returns one result per request, in order.
 * Lets many components (e.g. every product card of a PLP) ask for data that one backend call can
 * answer for all of them.
 */
export const createRequestBatcher = <TReq, TRes>({
  windowMs,
  maxBatchSize,
  getBatchKey,
  loadBatch,
}: {
  windowMs: number;
  maxBatchSize: number;
  getBatchKey: (req: TReq) => string;
  loadBatch: (reqs: TReq[]) => Promise<TRes[]>;
}) => {
  let pending: Pending<TReq, TRes>[] = [];
  let timer: ReturnType<typeof setTimeout> | undefined;

  const loadChunk = (requests: Pending<TReq, TRes>[]) =>
    loadBatch(requests.map((p) => p.req)).then(
      (results) => requests.forEach((p, i) => p.resolve(results[i] as TRes)),
      (error: unknown) => requests.forEach((p) => p.reject(error))
    );

  const flush = () => {
    const queued = pending;
    pending = [];
    timer = undefined;

    // a plain Map instead of Map.groupBy, which older Safari versions lack
    const byKey = new Map<string, Pending<TReq, TRes>[]>();
    queued.forEach((p) => {
      const key = getBatchKey(p.req);
      byKey.set(key, [...(byKey.get(key) ?? []), p]);
    });
    byKey.forEach((requests) => chunk(requests, maxBatchSize).forEach((c) => void loadChunk(c)));
  };

  return {
    load: (req: TReq) =>
      new Promise<TRes>((resolve, reject) => {
        pending.push({ req, resolve, reject });
        timer ??= setTimeout(flush, windowMs);
      }),
  };
};
